import { useQueries, useQuery } from '@tanstack/react-query';
import { authUtil } from 'src/utils';

/**
 * The chapter list for a critique group's linked book.
 *
 * The critique endpoints expose only `{_id, title, author}` for a linked book,
 * so the chapter list comes from the existing `GET /books/:bookId` — which is
 * reader-accessible (it checks author → account member → reader), and a
 * critique member always holds a provisioned Reader on the linked book.
 *
 * This is presentation scaffolding ONLY — it decides which chapters to list and
 * what to call them. It grants no visibility: whether a chapter's feedback is
 * revealed is decided by the feedback endpoint alone.
 */

export type CritiqueBookPart = {
  _id: string;
  title?: string;
};

const apiBase = () =>
  `${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}`;

type BookResponse = {
  _id: string;
  title?: string;
  content?: Array<{
    _id: string;
    versionNumber?: number;
    parts?: Array<{ _id: string; title?: string; type?: string }>;
  }>;
};

/**
 * Parts of the book's CURRENT content version (the highest versionNumber, which
 * is also what a new reader is pinned to). A member pinned to an older version
 * may therefore see a chapter list that differs from what they're reading; the
 * lock state stays correct regardless, since it's keyed on part ids from the
 * server. Reconciling per-reader versions is deferred (noted in the plan).
 */
const fetchBookParts = async (bookId: string): Promise<CritiqueBookPart[]> => {
  const response = await fetch(`${apiBase()}/books/${bookId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'br-token': await authUtil.getFreshIdToken(),
    },
  });
  if (!response.ok) throw new Error('Failed to fetch book');
  const book: BookResponse = await response.json();
  const versions = book.content || [];
  const current = versions.reduce(
    (best, v) => ((v.versionNumber ?? 0) >= (best?.versionNumber ?? -1) ? v : best),
    versions[0]
  );
  return (current?.parts || []).map(p => ({ _id: p._id, title: p.title }));
};

const partsQueryOptions = (bookId?: string) => ({
  queryKey: ['critiqueBookParts', bookId] as const,
  queryFn: () => fetchBookParts(bookId as string),
  refetchOnWindowFocus: false,
  staleTime: 1000 * 60,
  // A failed book read shouldn't break the feedback view — the caller falls
  // back to the parts it can infer from the feedback payload.
  retry: 1,
  enabled: !!bookId,
});

export const useCritiqueBookParts = (bookId?: string) =>
  useQuery<CritiqueBookPart[]>(partsQueryOptions(bookId));

export type CritiqueBookPartsResult = {
  bookId: string;
  parts: CritiqueBookPart[];
  isLoading: boolean;
  isError: boolean;
};

/**
 * The same list for EVERY book linked to a group, so the checkpoint editor can
 * offer a part from any of them (the server accepts a part from any linked
 * book). Shares `partsQueryOptions` — and therefore the cache — with the
 * single-book hook above.
 *
 * `enabled: false` keeps the modal from reading every linked book until a
 * deliverable type actually needs a part. Note that a *disabled* v4 query still
 * reports `isLoading: true` (status stays 'loading' while fetchStatus is
 * 'idle'), so the caller would render a permanent spinner — `isInitialLoading`
 * is the one that means "actually fetching right now".
 */
export const useCritiqueBooksParts = (
  bookIds: string[],
  options: { enabled?: boolean } = {}
): CritiqueBookPartsResult[] => {
  const enabled = options.enabled !== false;
  const results = useQueries({
    queries: bookIds.map(id => {
      const opts = partsQueryOptions(id);
      return { ...opts, enabled: opts.enabled && enabled };
    }),
  });

  return results.map((result, i) => ({
    bookId: bookIds[i],
    parts: (result.data as CritiqueBookPart[] | undefined) || [],
    isLoading: result.isInitialLoading,
    isError: result.isError,
  }));
};

export default useCritiqueBookParts;
